import Logger from "@/helpers/logger";
import billingApi from "./billing.api";
import store from "@/store";
import { PRODUCTS } from "@/helpers/product";
import {
  getDomainDetailsFromCurrentPlan,
  getMailSuiteDetailsFromCurrentPlan,
  getSiteDetailsFromCurrentPlan,
  processPlansWithPricingConfig,
} from "@/helpers/billingHelpers";
import { getBundleId, getDefaultCurrency, getDomainName, isNeoBrand } from "@/helpers";
import { isPrepaidCard } from "@/helpers/stripe";
import { createReactivateOrderPayload } from "@/helpers/payment/bundlePaymentHelpers";

class BillingService {
  /**
   * It is necessary to pass domainName here because when a user logs in, the login response contains the primaryDomain.
   * We log the user into that account and find the bundle id from the bundles array in the login response.
   * However, the bundles list is limited to 100 items, so if the primary domain is not found in that list, everything fails and the user cannot use the control panel.
   * By passing domainName to getCurrentPlan, we ensure the backend can return the correct bundle id for that domain, so all further API calls that require the bundle id will work reliably.
   * The backend team has been informed about this limitation.
   */
  async getCurrentPlan() {
    try {
      const data = await billingApi.getCurrentPlan({
        domainName: getDomainName(),
      });

      const payload = {
        ...data,
        // at present it is possible that currency can be empty in current plan
        // in such case default/USD current should be used
        currency: data.currency || getDefaultCurrency(),
        ...getMailSuiteDetailsFromCurrentPlan(data),
        ...getSiteDetailsFromCurrentPlan(data),
        ...getDomainDetailsFromCurrentPlan(data),
      };

      return Promise.resolve(payload);
    } catch (e) {
      Logger.error("`getCurrentPlan` service", e);
      return Promise.reject(e);
    }
  }

  async getAllPlans(currency, options = {}) {
    try {
      const data = await billingApi.getAllPlans(currency);

      const plansGroupedByOrderType = data.plans.reduce((acc, order) => {
        // incase `orderType` missing (specifically for old data) then consider that as mail plan
        const { orderType = PRODUCTS.MAIL_SUITE } = order;
        acc[orderType] = acc[orderType] || [];
        acc[orderType].push(order);
        return acc;
      }, {});

      // Extract pricing config IDs from current plan if available
      const pricingConfigIds = options.pricingConfigIds || {};

      // Apply pricing configurations to plans if config IDs are provided
      let processedPlans = plansGroupedByOrderType;
      if (Object.keys(pricingConfigIds).length > 0) {
        try {
          const pricingConfigs = await billingApi.getAllPricingConfigs();
          processedPlans = processPlansWithPricingConfig(
            plansGroupedByOrderType,
            pricingConfigIds,
            pricingConfigs
          );
        } catch (pricingError) {
          Logger.error("`getAllPricingConfigs` failed - treating as critical", pricingError);
          // Re-throw so that user gets logged out
          throw new Error(`Pricing config fetch failed: ${pricingError}`);
        }
      }

      const mailPlanList = processedPlans[PRODUCTS.MAIL_SUITE] || [];
      const sitePlanList = processedPlans[PRODUCTS.SITE] || [];
      const domainPlanList = processedPlans[PRODUCTS.DOMAIN] || [];

      store.dispatch("mail/plans", mailPlanList);
      store.dispatch("site/plans", sitePlanList);
      store.dispatch("domain/plans", domainPlanList);

      return {
        [PRODUCTS.MAIL_SUITE]: mailPlanList,
        [PRODUCTS.SITE]: sitePlanList,
      };
    } catch (e) {
      Logger.error("`getAllPlans` service", e);
      return Promise.reject(e);
    }
  }

  async getAllAddons() {
    try {
      const data = await billingApi.getAllAddons();
      return data;
    } catch (e) {
      Logger.error("`getAllAddons` service", e);
      return Promise.reject(e);
    }
  }

  async getCardDetails({ syncWithStore = true } = {}) {
    try {
      const data = await billingApi.getCardDetails();
      syncWithStore && store.dispatch("payment/savedCard", data);
      return data;
    } catch (err) {
      if (err.data?.code === "CARD_NOT_FOUND") {
        store.dispatch("payment/savedCard", null);
      }

      Logger.error("`getCardDetails` service", err);
      return Promise.resolve(null);
    }
  }

  checkPaymentType(action) {
    return billingApi.checkPaymentType(action);
  }

  async calculateBundleCharges(requestPayload) {
    try {
      const data = await billingApi.calculateBundleCharges(requestPayload);
      return data;
    } catch (e) {
      Logger.error("`calculateBundleCharges` service", e);
      return Promise.reject(e);
    }
  }

  async reactivateOrder({ orderId }) {
    try {
      const payload = createReactivateOrderPayload({
        orderId,
        bundle: { bundleId: getBundleId() },
      });
      return await billingApi.calculateBundleCharges(payload);
    } catch (e) {
      Logger.error("`reactivateOrder` service", e);
      return Promise.reject(e);
    }
  }

  renewPlan(autoRenew, otherParams = {}) {
    try {
      return billingApi.renewPlan({
        autoRenew,
        ...otherParams,
      });
    } catch (e) {
      Logger.error("`updateConfig - renewPlan` service", e);
      return Promise.reject(e);
    }
  }

  changeBillingCycle(renewalBillingCycle) {
    try {
      return billingApi.changeBillingCycle({
        renewalBillingCycle,
      });
    } catch (e) {
      Logger.error("`updateConfig - changeBillingCycle` service", e);
      return Promise.reject(e);
    }
  }

  changeBillingDate(customExpiryDate) {
    try {
      return billingApi.changeBillingDate({ customExpiryDate });
    } catch (e) {
      Logger.error("`changeBillingDate` service", e);
      return Promise.reject(e);
    }
  }

  getAddCardIntent() {
    try {
      return billingApi.getAddCardIntent();
    } catch (e) {
      Logger.error("`getAddCardIntent` service", e);
      return Promise.reject(e);
    }
  }

  async deleteCards(payload) {
    try {
      return await billingApi.deleteCards(payload);
    } catch (e) {
      Logger.error("`deleteCards` service", e);
      return Promise.reject(e);
    }
  }

  async deleteIfPrepaidCard(cardDetails) {
    const { funding, paymentMethodId } = cardDetails || {};
    const _isPrepaidCard = isPrepaidCard(funding);

    // if not prepaid card or not neo brand then return false
    if (!_isPrepaidCard || !isNeoBrand()) {
      return Promise.resolve({ prepaidCard: false, cardDeleted: false });
    }

    try {
      // delete the newly added prepaid card
      await billingService.deleteCards({
        paymentMethodIds: [paymentMethodId],
        bundleId: getBundleId(),
      });
      return Promise.resolve({
        prepaidCard: true,
        cardDeleted: true,
      });
    } catch (e) {
      // failed to delete the prepaid card
      // in this case if user refresh page then he can use this case for payment completion
      console.error("Failed to delete prepaid card", e);
      return Promise.resolve({
        prepaidCard: true,
        cardDeleted: false,
      });
    }
  }

  async getTaxDetails() {
    try {
      return await billingApi.getTaxDetails();
    } catch (e) {
      Logger.error("`getTaxDetails` service", e);
      return Promise.reject(e);
    }
  }

  async getBillingAddress() {
    try {
      return await billingApi.getBillingAddress();
    } catch (e) {
      Logger.error("`getBillingAddress` service", e);
      return Promise.reject(e);
    }
  }

  async setBillingAddress(payload) {
    try {
      return await billingApi.setBillingAddress(payload);
    } catch (e) {
      Logger.error("`setBillingAddress` service", e);
      return Promise.reject(e);
    }
  }

  async updateAddonConfig(payload) {
    try {
      return await billingApi.updateAddonConfig(payload);
    } catch (e) {
      Logger.error("`updateAddonConfig` service", e);
      return Promise.reject(e);
    }
  }

  async initBundlePayment(requestPayload) {
    try {
      const data = await billingApi.initBundlePayment(requestPayload);
      return data;
    } catch (e) {
      Logger.error("`initBundlePayment` service", e);
      return Promise.reject(e);
    }
  }

  async completeBundlePayment(requestPayload) {
    try {
      const data = await billingApi.completeBundlePayment(requestPayload);
      return data;
    } catch (e) {
      Logger.error("`completeBundlePayment` service", e);
      return Promise.reject(e);
    }
  }
}

const billingService = new BillingService();

export default billingService;
